import { Formatters, Messages } from '../../codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '../../commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { L10n } from '../../ts/L10n/L10n';
import { Set } from '../../ts/models/Set';
import { getSetFlowParametersDialog } from '../components/SetFlowParametersDialog';
import { FlowBaseInfo } from '../models/FlowInfo';
import { Flows } from '../resources/Flows';
import { FlowErrors } from '../services/FlowErrors';
import { FlowParameterParams,RunFlowParams } from '../types/FlowParameters';
import { areFlowParametersEnabled } from '../utils/FlowParametersUtils';

export function showRunFlowDialog(flows: Set<FlowBaseInfo>, isRunAllIncremental: boolean = null): Promise<IResult> {
  let message: string;

  if (flows.size() === 1) {
    const flow = flows.first();
    message = Messages.runFlowsSinglePrompt({ FLOW_NAME: L10n.elideIfLong(flow.name) });
  } else {
    message = Messages.runFlowsMultiplePrompt({ ITEM_COUNT_LOC: Formatters.formatCount(flows.size()) });
  }

  const parameterCallback = (id: string, parameterList: RunFlowParams[]) => (map: {[key: string]: string}): Promise<any> => {
    const parametersForFlow = Object.keys(map).map((key): FlowParameterParams => {
      return {
        id: key,
        overrideValue: map[key],
      };
    });
    parameterList.push({
      id: id,
      parameterParams: parametersForFlow,
    });
    return Promise.resolve();
  };

  // We need to chain the dialogs after one another
  const parameterPromiseChainer = (flows: FlowBaseInfo[], current: number, parameterList: RunFlowParams[]): Promise<any> => {
    // Check if we are at the end of the list
    if (current >= flows.length) {
      return Promise.resolve();
    }
    // If no parameters, check next flow
    if (flows[current].relevantParameters.length === 0) {
      return parameterPromiseChainer(flows, current + 1, parameterList);
    }

    return getSetFlowParametersDialog(
      flows[current].name,
      flows[current].relevantParameters,
      parameterCallback(flows[current].id, parameterList)
    ).then(() => {
      if (current + 1 < flows.length) {
        return parameterPromiseChainer(flows, current + 1, parameterList);
      }
      return Promise.resolve();
    });
  };

  const action = () => {
    if (areFlowParametersEnabled()) {
      const parameterList: RunFlowParams[] = [];
      return parameterPromiseChainer(flows.toArray(), 0, parameterList).then(() => {
        return Flows.runFlow(flows.keys(), isRunAllIncremental, parameterList);
      });
    } else {
      return Flows.runFlow(flows.keys(), isRunAllIncremental);
    }
  };

  const dialogInstance = openReactConfirmationDialog<void, IResult>({
    title: Messages.runNow(),
    message: message,
    paramsForAction: undefined,
    actionName: Messages.runNow(),
    action: action,
    dialogWidth: TabDialogWidth.width375px,
  });

  dialogInstance.result.then((result: IResult) => {
    ActionHelpers.displayStandardToastsForResult(
      flows,
      result,
      {
        success: standardActionSuccessMsg(Messages.runFlowsSuccess),
        error: standardActionErrorMsg(Messages.runFlowsError),
      },
      (errorItem: IErrorItem<FlowBaseInfo>) => FlowErrors.errorDetailsFor(errorItem)
    );
  });

  return Promise.resolve(dialogInstance.result);
}
